import {
  maskCEP,
  maskCNPJ,
  maskCPF,
  onlyCnpjChars,
  validateCEP,
  validateCNPJ,
  validateCPF,
} from '../../../shared/utils/validators';
import type {
  CreateSupplierPayload,
  Supplier,
  SupplierPixKeyType,
  SupplierPortalUpdatePayload,
} from '../api';
import { bankByCode } from '../constants/banks';

export type SupplierFormErrors = Partial<Record<keyof CreateSupplierPayload, string>>;

export const SUPPLIER_PORTAL_EDITABLE_FIELDS = [
  'tradeName',
  'stateRegistration',
  'municipalRegistration',
  'taxRegime',
  'contactName',
  'contactEmail',
  'contactPhone',
  'bankName',
  'bankCode',
  'bankAgency',
  'bankAccount',
  'bankAccountType',
  'bankPixKeyType',
  'bankPixKey',
  'addressZipCode',
  'addressStreet',
  'addressNumber',
  'addressComplement',
  'addressDistrict',
  'addressCity',
  'addressState',
  'addressCountry',
  'categories',
  'services',
] as const satisfies readonly (keyof SupplierPortalUpdatePayload)[];

const supplierPortalEditableFields = new Set<keyof CreateSupplierPayload>(
  SUPPLIER_PORTAL_EDITABLE_FIELDS,
);

const UF_CODES = new Set([
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO',
]);

const BUSINESS_TEXT_PATTERN = /^[\p{L}\p{M}\d][\p{L}\p{M}\d &'’.,()\/-]*$/u;
const PERSON_NAME_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M}'’. \-]*$/u;
const ADDRESS_TEXT_PATTERN = /^[\p{L}\p{M}\d][\p{L}\p{M}\d ºª°#'’.,()\/-]*$/u;
const LOCALITY_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M} '’.\-]*$/u;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u;
const EMAIL_LOCAL_PATTERN = /^[\p{L}\p{N}!#$%&'*+/=?^_`{|}~.\-]+$/u;
const PIX_RANDOM_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const BANK_ACCOUNT_TYPES = new Set(['corrente', 'poupanca', 'pagamento']);

const trimmed = (value?: string | null) => value?.trim() ?? '';
const compactSpaces = (value?: string | null) => trimmed(value).replace(/\s+/g, ' ');
const digitsOnly = (value?: string | null) => (value ?? '').replace(/\D/g, '');
const hasAtLeastTwoLetters = (value: string) => (value.match(/\p{L}/gu) ?? []).length >= 2;

export function maskBrazilianPhone(value: string): string {
  const digits = digitsOnly(value).slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;

  const ddd = digits.slice(0, 2);
  const number = digits.slice(2);
  const splitAt = digits.length === 11 ? 5 : 4;
  if (number.length <= splitAt) return `(${ddd}) ${number}`;
  return `(${ddd}) ${number.slice(0, splitAt)}-${number.slice(splitAt)}`;
}

export function maskSupplierDocument(value: string, supplierType: CreateSupplierPayload['supplierType']): string {
  return supplierType === 'pessoa_juridica' ? maskCNPJ(value) : maskCPF(value);
}

export function maskPixKey(value: string, type?: SupplierPixKeyType): string {
  if (type === 'cpf') return maskCPF(value);
  if (type === 'cnpj') return maskCNPJ(value);
  if (type === 'phone') {
    const digits = digitsOnly(value);
    const localDigits = digits.startsWith('55') && (digits.length === 12 || digits.length === 13)
      ? digits.slice(2)
      : digits;
    return maskBrazilianPhone(localDigits);
  }
  if (type === 'random') return value.toLowerCase().replace(/[^0-9a-f-]/g, '').slice(0, 36);
  return value;
}

export function normalizeSupplierPayload(payload: CreateSupplierPayload): CreateSupplierPayload {
  const pixType = payload.bankPixKeyType;
  const rawPix = trimmed(payload.bankPixKey);
  const pixPhoneDigits = pixType === 'phone'
    ? digitsOnly(rawPix).replace(/^55(?=\d{10,11}$)/, '')
    : '';

  return {
    ...payload,
    documentNumber: payload.supplierType === 'pessoa_juridica'
      ? onlyCnpjChars(payload.documentNumber)
      : digitsOnly(payload.documentNumber),
    corporateName: compactSpaces(payload.corporateName),
    tradeName: compactSpaces(payload.tradeName) || undefined,
    stateRegistration: trimmed(payload.stateRegistration) || undefined,
    municipalRegistration: trimmed(payload.municipalRegistration) || undefined,
    taxRegime: compactSpaces(payload.taxRegime) || undefined,
    contactName: compactSpaces(payload.contactName),
    contactEmail: trimmed(payload.contactEmail).toLowerCase(),
    contactPhone: digitsOnly(payload.contactPhone),
    bankCode: trimmed(payload.bankCode) || undefined,
    bankName: compactSpaces(payload.bankName) || undefined,
    bankAgency: trimmed(payload.bankAgency).toUpperCase() || undefined,
    bankAccount: trimmed(payload.bankAccount).toUpperCase() || undefined,
    bankAccountType: trimmed(payload.bankAccountType).toLowerCase() || undefined,
    bankPixKeyType: pixType,
    bankPixKey: rawPix
      ? pixType === 'cpf'
        ? digitsOnly(rawPix)
        : pixType === 'cnpj'
          ? onlyCnpjChars(rawPix)
          : pixType === 'email'
            ? rawPix.toLowerCase()
            : pixType === 'phone'
              ? pixPhoneDigits
              : rawPix.toLowerCase()
      : undefined,
    addressZipCode: digitsOnly(payload.addressZipCode) || undefined,
    addressStreet: compactSpaces(payload.addressStreet) || undefined,
    addressNumber: compactSpaces(payload.addressNumber).toUpperCase() || undefined,
    addressComplement: compactSpaces(payload.addressComplement) || undefined,
    addressDistrict: compactSpaces(payload.addressDistrict) || undefined,
    addressCity: compactSpaces(payload.addressCity) || undefined,
    addressState: trimmed(payload.addressState).toUpperCase() || undefined,
    addressCountry: compactSpaces(payload.addressCountry) || undefined,
    categories: payload.categories?.map(compactSpaces).filter(Boolean),
    services: payload.services?.map(compactSpaces).filter(Boolean),
    notes: trimmed(payload.notes) || undefined,
  };
}

function isValidEmail(value: string): boolean {
  if (!EMAIL_PATTERN.test(value) || value.length > 254) return false;
  const [local, domain] = value.split('@');
  if (
    !local
    || !domain
    || local.length > 64
    || domain.length > 253
    || value.includes('..')
    || local.startsWith('.')
    || local.endsWith('.')
    || !EMAIL_LOCAL_PATTERN.test(local)
  ) return false;

  const domainLabels = domain.split('.');
  const topLevelDomain = domainLabels.at(-1) ?? '';
  return topLevelDomain.length >= 2
    && /\p{L}/u.test(topLevelDomain)
    && domainLabels.every((label) => (
    label.length > 0
    && label.length <= 63
    && !label.startsWith('-')
    && !label.endsWith('-')
    && /^[\p{L}\p{N}-]+$/u.test(label)
    ));
}

function isValidBrazilianPhone(value: string): boolean {
  const digits = digitsOnly(value).replace(/^55(?=\d{10,11}$)/, '');
  if (!/^\d{10,11}$/.test(digits)) return false;
  const ddd = Number(digits.slice(0, 2));
  if (ddd < 11 || ddd > 99) return false;
  return digits.length === 11 ? digits[2] === '9' : /^[2-5]/.test(digits.slice(2));
}

function validateLength(
  errors: SupplierFormErrors,
  field: keyof CreateSupplierPayload,
  value: string | undefined,
  max: number,
  label: string,
) {
  if (value && value.length > max) errors[field] = `${label} deve ter no máximo ${max} caracteres.`;
}

export function validateSupplierForm(rawPayload: CreateSupplierPayload): SupplierFormErrors {
  const payload = normalizeSupplierPayload(rawPayload);
  const errors: SupplierFormErrors = {};

  if (!payload.documentNumber) {
    errors.documentNumber = payload.supplierType === 'pessoa_juridica' ? 'CNPJ é obrigatório.' : 'CPF é obrigatório.';
  } else if (payload.supplierType === 'pessoa_juridica' && !validateCNPJ(payload.documentNumber)) {
    errors.documentNumber = 'CNPJ inválido. Verifique o formato e os dígitos verificadores.';
  } else if (payload.supplierType === 'pessoa_fisica' && !validateCPF(payload.documentNumber)) {
    errors.documentNumber = 'CPF inválido. Verifique o formato e os dígitos verificadores.';
  }

  if (!payload.corporateName) {
    errors.corporateName = 'Razão Social / Nome é obrigatório.';
  } else if (!BUSINESS_TEXT_PATTERN.test(payload.corporateName) || !hasAtLeastTwoLetters(payload.corporateName)) {
    errors.corporateName = 'Informe uma Razão Social / Nome com caracteres válidos.';
  }
  validateLength(errors, 'corporateName', payload.corporateName, 180, 'Razão Social / Nome');

  if (payload.tradeName && (!BUSINESS_TEXT_PATTERN.test(payload.tradeName) || !hasAtLeastTwoLetters(payload.tradeName))) {
    errors.tradeName = 'Nome Fantasia contém caracteres inválidos.';
  }
  validateLength(errors, 'tradeName', payload.tradeName, 180, 'Nome Fantasia');

  if (!payload.contactName) {
    errors.contactName = 'Contato é obrigatório.';
  } else if (!PERSON_NAME_PATTERN.test(payload.contactName) || !hasAtLeastTwoLetters(payload.contactName)) {
    errors.contactName = 'Contato deve conter apenas letras e pontuação de nome.';
  }
  validateLength(errors, 'contactName', payload.contactName, 100, 'Contato');

  if (!payload.contactEmail) {
    errors.contactEmail = 'E-mail é obrigatório.';
  } else if (!isValidEmail(payload.contactEmail)) {
    errors.contactEmail = 'Informe um e-mail válido com até 254 caracteres.';
  }

  if (!payload.contactPhone) {
    errors.contactPhone = 'Telefone é obrigatório.';
  } else if (!isValidBrazilianPhone(payload.contactPhone)) {
    errors.contactPhone = 'Informe um telefone brasileiro válido com DDD e 10 ou 11 dígitos.';
  }

  validateLength(errors, 'stateRegistration', payload.stateRegistration, 30, 'Inscrição Estadual');
  validateLength(errors, 'municipalRegistration', payload.municipalRegistration, 30, 'Inscrição Municipal');
  validateLength(errors, 'taxRegime', payload.taxRegime, 60, 'Regime Fiscal');

  if (payload.riskScore != null && (!Number.isInteger(payload.riskScore) || payload.riskScore < 0 || payload.riskScore > 100)) {
    errors.riskScore = 'Score de Risco deve ser um número inteiro entre 0 e 100.';
  }

  const catalogBank = bankByCode(payload.bankCode);
  if (payload.bankCode && !/^\d{3}$/.test(payload.bankCode)) {
    errors.bankCode = 'Código COMPE deve possuir 3 dígitos.';
  } else if (payload.bankName && !payload.bankCode) {
    errors.bankCode = 'Selecione ou informe o código COMPE do banco.';
  }
  if (payload.bankCode && !payload.bankName) {
    errors.bankName = 'Informe a instituição correspondente ao código COMPE.';
  } else if (payload.bankName && (!BUSINESS_TEXT_PATTERN.test(payload.bankName) || !hasAtLeastTwoLetters(payload.bankName))) {
    errors.bankName = 'Instituição bancária contém caracteres inválidos.';
  } else if (
    payload.bankCode
    && payload.bankName
    && catalogBank
    && catalogBank.name.toLocaleLowerCase('pt-BR') !== payload.bankName.toLocaleLowerCase('pt-BR')
  ) {
    errors.bankName = `O código COMPE ${payload.bankCode} corresponde a ${catalogBank.name}.`;
  }
  validateLength(errors, 'bankName', payload.bankName, 120, 'Instituição bancária');
  if (payload.bankAgency && !/^[0-9A-Z-]{1,20}$/.test(payload.bankAgency)) {
    errors.bankAgency = 'Agência deve ter até 20 caracteres alfanuméricos e dígito opcional.';
  }
  if (payload.bankAccount && !/^[0-9A-Z-]{1,30}$/.test(payload.bankAccount)) {
    errors.bankAccount = 'Conta deve ter até 30 caracteres alfanuméricos e dígito opcional.';
  }
  if (payload.bankAccountType && !BANK_ACCOUNT_TYPES.has(payload.bankAccountType)) {
    errors.bankAccountType = 'Selecione um tipo de conta válido.';
  }

  if (payload.bankPixKeyType && !payload.bankPixKey) {
    errors.bankPixKey = 'Informe a chave PIX correspondente ao tipo selecionado.';
  } else if (payload.bankPixKey && !payload.bankPixKeyType) {
    errors.bankPixKeyType = 'Selecione o tipo da chave PIX.';
  } else if (payload.bankPixKey && payload.bankPixKeyType) {
    const pix = payload.bankPixKey;
    if (payload.bankPixKeyType === 'cpf' && !validateCPF(pix)) {
      errors.bankPixKey = 'Informe uma chave PIX CPF válida.';
    } else if (payload.bankPixKeyType === 'cnpj' && !validateCNPJ(pix)) {
      errors.bankPixKey = 'Informe uma chave PIX CNPJ válida.';
    } else if (payload.bankPixKeyType === 'email' && !isValidEmail(pix)) {
      errors.bankPixKey = 'Informe uma chave PIX de e-mail válida.';
    } else if (payload.bankPixKeyType === 'phone' && !isValidBrazilianPhone(pix)) {
      errors.bankPixKey = 'Informe uma chave PIX de telefone brasileiro válida.';
    } else if (payload.bankPixKeyType === 'random' && !PIX_RANDOM_PATTERN.test(pix)) {
      errors.bankPixKey = 'Informe uma chave PIX aleatória no formato UUID.';
    }
  }
  validateLength(errors, 'bankPixKey', payload.bankPixKey, 254, 'Chave PIX');

  if (payload.addressZipCode && (!validateCEP(payload.addressZipCode) || /^0{8}$/.test(payload.addressZipCode))) {
    errors.addressZipCode = 'Informe um CEP válido no formato 00000-000.';
  }
  if (payload.addressStreet && (!ADDRESS_TEXT_PATTERN.test(payload.addressStreet) || !hasAtLeastTwoLetters(payload.addressStreet))) {
    errors.addressStreet = 'Logradouro contém caracteres inválidos.';
  }
  validateLength(errors, 'addressStreet', payload.addressStreet, 180, 'Logradouro');

  if (payload.addressNumber && !/^(?:S\/?N|[0-9][0-9A-Z .\/-]*)$/i.test(payload.addressNumber)) {
    errors.addressNumber = 'Número deve ser um endereço válido ou S/N.';
  }
  validateLength(errors, 'addressNumber', payload.addressNumber, 20, 'Número');
  validateLength(errors, 'addressComplement', payload.addressComplement, 120, 'Complemento');
  if (payload.addressDistrict && (!LOCALITY_PATTERN.test(payload.addressDistrict) || !hasAtLeastTwoLetters(payload.addressDistrict))) {
    errors.addressDistrict = 'Bairro deve conter um nome válido.';
  }
  validateLength(errors, 'addressDistrict', payload.addressDistrict, 100, 'Bairro');

  if (payload.addressCity && (!LOCALITY_PATTERN.test(payload.addressCity) || !hasAtLeastTwoLetters(payload.addressCity))) {
    errors.addressCity = 'Cidade deve conter um nome válido.';
  }
  validateLength(errors, 'addressCity', payload.addressCity, 100, 'Cidade');
  if (payload.addressState && !UF_CODES.has(payload.addressState)) {
    errors.addressState = 'Selecione uma UF válida.';
  }
  validateLength(errors, 'addressCountry', payload.addressCountry, 60, 'País');

  if ((payload.categories?.length ?? 0) > 20 || payload.categories?.some((item) => item.length > 80)) {
    errors.categories = 'Informe no máximo 20 categorias, com até 80 caracteres cada.';
  }
  if ((payload.services?.length ?? 0) > 30 || payload.services?.some((item) => item.length > 100)) {
    errors.services = 'Informe no máximo 30 serviços, com até 100 caracteres cada.';
  }
  validateLength(errors, 'notes', payload.notes, 1000, 'Observações');

  return errors;
}

export function supplierToPortalForm(supplier: Supplier): CreateSupplierPayload {
  return {
    supplierType: supplier.supplierType,
    documentNumber: supplier.documentNumber,
    corporateName: supplier.corporateName,
    tradeName: supplier.tradeName ?? '',
    stateRegistration: supplier.stateRegistration ?? '',
    municipalRegistration: supplier.municipalRegistration ?? '',
    taxRegime: supplier.taxRegime ?? '',
    contactName: supplier.contactName,
    contactEmail: supplier.contactEmail,
    contactPhone: maskBrazilianPhone(supplier.contactPhone),
    bankName: supplier.bankName ?? '',
    bankCode: supplier.bankCode ?? '',
    bankAgency: supplier.bankAgency ?? '',
    bankAccount: supplier.bankAccount ?? '',
    bankAccountType: supplier.bankAccountType ?? '',
    bankPixKeyType: supplier.bankPixKeyType ?? undefined,
    bankPixKey: supplier.bankPixKey
      ? maskPixKey(supplier.bankPixKey, supplier.bankPixKeyType ?? undefined)
      : '',
    addressZipCode: supplier.addressZipCode ? maskCEP(supplier.addressZipCode) : '',
    addressStreet: supplier.addressStreet ?? '',
    addressNumber: supplier.addressNumber ?? '',
    addressComplement: supplier.addressComplement ?? '',
    addressDistrict: supplier.addressDistrict ?? '',
    addressCity: supplier.addressCity ?? '',
    addressState: supplier.addressState ?? '',
    addressCountry: supplier.addressCountry ?? 'Brasil',
    categories: supplier.categories ?? [],
    services: supplier.services ?? [],
  };
}

export function validateSupplierPortalForm(payload: CreateSupplierPayload): SupplierFormErrors {
  return Object.fromEntries(
    Object.entries(validateSupplierForm(payload)).filter(([field]) => (
      supplierPortalEditableFields.has(field as keyof CreateSupplierPayload)
    )),
  ) as SupplierFormErrors;
}

export function shouldUseCustomBankFields(bankCode?: string | null, bankName?: string | null): boolean {
  const normalizedCode = trimmed(bankCode);
  const normalizedName = compactSpaces(bankName);
  if (!normalizedCode) return Boolean(normalizedName);
  const catalogBank = bankByCode(normalizedCode);
  if (!catalogBank) return true;
  return !normalizedName || catalogBank.name.localeCompare(
    normalizedName,
    'pt-BR',
    { sensitivity: 'base' },
  ) !== 0;
}

export function normalizeSupplierPortalPayload(
  rawPayload: CreateSupplierPayload,
): SupplierPortalUpdatePayload {
  const payload = normalizeSupplierPayload(rawPayload);
  return {
    tradeName: payload.tradeName ?? null,
    stateRegistration: payload.stateRegistration ?? null,
    municipalRegistration: payload.municipalRegistration ?? null,
    taxRegime: payload.taxRegime ?? null,
    contactName: payload.contactName,
    contactEmail: payload.contactEmail,
    contactPhone: payload.contactPhone,
    bankName: payload.bankName ?? null,
    bankCode: payload.bankCode ?? null,
    bankAgency: payload.bankAgency ?? null,
    bankAccount: payload.bankAccount ?? null,
    bankAccountType: payload.bankAccountType ?? null,
    bankPixKeyType: payload.bankPixKeyType ?? null,
    bankPixKey: payload.bankPixKey ?? null,
    addressZipCode: payload.addressZipCode ?? null,
    addressStreet: payload.addressStreet ?? null,
    addressNumber: payload.addressNumber ?? null,
    addressComplement: payload.addressComplement ?? null,
    addressDistrict: payload.addressDistrict ?? null,
    addressCity: payload.addressCity ?? null,
    addressState: payload.addressState ?? null,
    addressCountry: payload.addressCountry ?? null,
    categories: payload.categories ?? [],
    services: payload.services ?? [],
  };
}

export { maskCEP };
